import { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { useLogs } from '../context/LogContext';
import '../css/Dashboard.css';
import '../css/Notifications.css';
import useIdleLogout from "../hooks/useIdleLogout";

/* ---------------- Pretty labels ---------------- */
const prettyChannel = { sms: 'SMS', email: 'Email', inApp: 'In App' };
const AUDIENCE_KEYS = ['admins', 'generalUsers', 'merchants', 'drivers', 'finance', 'organizers'];
const prettyAudience = {
  admins:       'Admins',
  generalUsers: 'General Users',
  merchants:    'Merchants',
  drivers:      'Drivers',
  finance:      'Finance',
  organizers:   'Organizers',
};

/* ---------------- Env helpers ---------------- */
const envPick = (k, fb = '') =>
  (typeof import.meta !== 'undefined' && import.meta.env?.[k]) ??
  (typeof process !== 'undefined' && process.env?.[k]) ??
  (typeof window !== 'undefined' && window.__ENV__?.[k]) ??
  fb;

// Base used by your Login page
const API_BASE_URL = (envPick('REACT_APP_API_BASE_URL', '') || '').replace(/\/+$/, '');

// Notification endpoints
const API_BASE = (envPick('REACT_APP_ADMIN_API_BASE', '') || '').replace(/\/+$/, '');
const SYSTEM_NOTIFICATIONS =
  envPick('REACT_APP_SYSTEM_NOTIFICATIONS') ||
  envPick('VITE_SYSTEM_NOTIFICATIONS') ||
  (API_BASE ? `${API_BASE}/admin/api/system-notifications` : 'https://backend.tabdhey.bt/admin/api/system-notifications');

const SYSTEM_NOTIFICATIONS_ALL =
  envPick('REACT_APP_SYSTEM_NOTIFICATIONS_ALL') ||
  envPick('VITE_SYSTEM_NOTIFICATIONS_ALL') ||
  (API_BASE ? `${API_BASE}/admin/api/system-notifications/all` : 'https://backend.tabdhey.bt/admin/api/system-notifications/all');

// Individual user notification endpoints from backend:


/* ---------------- Auth / Identity helpers ---------------- */
const getLS = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const setLS = (k, v) => { try { localStorage.setItem(k, v); } catch { } };
const parseJSON = (raw) => { try { return raw ? JSON.parse(raw) : null; } catch { return null; } };

const readToken = () => (
  getLS('access_token') ||
  getLS('token') ||
  getLS('adminToken') ||
  getLS('accessToken') ||
  getLS('authToken') ||
  ''
).trim();

function getAuthHeaders(withJson = false) {
  const token = readToken();
  const headers = { Accept: 'application/json' };

  if (withJson) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  return headers;
}

async function apiFetch(url, { method = 'GET', body, signal } = {}) {
  const upper = String(method || 'GET').toUpperCase();
  const hasBody = body !== undefined && body !== null && upper !== 'GET';

  const res = await fetch(url, {
    method: upper,
    headers: getAuthHeaders(hasBody),
    ...(hasBody ? { body: JSON.stringify(body) } : {}),
    ...(signal ? { signal } : {}),
    credentials: USE_CREDENTIALS ? 'include' : 'omit',
    mode: 'cors',
  });

  if (!res.ok) {
    const msg = await readError(res);
    if (res.status === 401) throw new Error(`Unauthorized (401): Please login again. ${msg}`);
    if (res.status === 403) throw new Error(`Forbidden (403): You do not have permission. ${msg}`);
    throw new Error(msg);
  }

  if (res.status === 204) return null;

  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return await res.json();

  return await res.text();
}

function decodeJwt(jwt) {
  if (!jwt || jwt.split('.').length < 2) return {};
  try {
    const b = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const j = decodeURIComponent(atob(b).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(j) || {};
  } catch { return {}; }
}

function readAnyUserObject() {
  return (
    parseJSON(getLS('user')) ||
    parseJSON(getLS('admin')) ||
    parseJSON(getLS('profile')) ||
    parseJSON(getLS('currentUser')) ||
    {}
  );
}

function readUserIdFromStorageOrJwt() {
  const direct = getLS('user_id') || getLS('admin_id');
  if (direct && Number.isFinite(Number(direct))) return Number(direct);

  const u = readAnyUserObject();
  const fromObj = u?.user_id ?? u?.id ?? u?._id;
  if (Number.isFinite(Number(fromObj))) return Number(fromObj);

  const p = decodeJwt(readToken());
  const fromJwt = p?.user_id ?? p?.id ?? p?.sub;
  if (Number.isFinite(Number(fromJwt))) return Number(fromJwt);

  return NaN;
}

function readUserNameFromStorageOrJwt() {
  const lsName = getLS('user_name');
  if (lsName) return lsName;

  const u = readAnyUserObject();
  const objName = u?.user_name || u?.name || u?.full_name || u?.username;
  if (objName) return String(objName);

  const p = decodeJwt(readToken());
  const jwtName = p?.user_name || p?.name || p?.preferred_username || p?.given_name;
  if (jwtName) return String(jwtName);

  return '';
}

/** Ensure we have {user_id, user_name}; fetch /api/profile/:id if name is missing. */
async function ensureIdentity() {
  let user_id = readUserIdFromStorageOrJwt();
  let user_name = readUserNameFromStorageOrJwt();

  // If we have id but no name, try profile fetch once
  if (API_BASE_URL && Number.isFinite(user_id) && user_id > 0 && !user_name) {
    try {
      const res = await fetch(`${API_BASE_URL}/api/profile/${user_id}`, {
        headers: getAuthHeaders(false),
      });
      if (res.ok) {
        const prof = await res.json();
        user_name = prof?.data?.user_name || prof?.user_name || user_name || '';
        if (user_name) setLS('user_name', user_name);
      }
    } catch { /* ignore */ }
  }

  // cache for other screens
  if (Number.isFinite(user_id) && user_id > 0) {
    setLS('user_id', String(user_id));
    setLS('admin_id', String(user_id));
  }
  if (user_name) setLS('user_name', user_name);

  return { user_id, user_name };
}

/* ---------------- Small utils ---------------- */
const USE_CREDENTIALS = false; // set to true only if API needs cookies
const readError = async (res) => {
  const ct = res.headers.get('content-type') || '';
  try {
    if (ct.includes('application/json')) {
      const j = await res.json();
      return j.message || j.error || JSON.stringify(j);
    }
    const t = await res.text();
    return t || `HTTP ${res.status}`;
  } catch { return `HTTP ${res.status}`; }
};

const toArray = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') {
    try { const j = JSON.parse(v); if (Array.isArray(j)) return j; } catch { }
    return v.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [];
};
const backPrettyChan = (v) => (v === 'in_app' ? 'In App' : (v === 'sms' ? 'SMS' : (v === 'email' ? 'Email' : v)));
const backPrettyAud = (v) => (v === 'admin' ? 'Admins' : (v === 'user' ? 'General Users' : (v === 'merchant' ? 'Merchants' : (v === 'driver' ? 'Drivers' : v))));

/* ---------------- CSV Export ---------------- */
function csvEscape(cell) {
  const s = String(cell ?? '');
  const needsQuote = /[",\n]/.test(s);
  const esc = s.replace(/"/g, '""');
  return needsQuote ? `"${esc}"` : esc;
}
function downloadCSV(filename, rows) {
  const BOM = '\uFEFF'; // Excel-friendly UTF-8 BOM
  const blob = new Blob([BOM + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ---------------- Component ---------------- */
export default function Notifications() {
  useIdleLogout();
  const { logActivity } = useLogs();

  // identity cache for this screen
  const [identity, setIdentity] = useState({ user_id: NaN, user_name: '' });

  // channels
  const [channels, setChannels] = useState({ sms: false, email: false, inApp: false });

  // audience
  const [audience, setAudience] = useState({ admins: false, generalUsers: false, merchants: false, drivers: false, finance: false, organizers: false });
  const allAudienceSelected = useMemo(() => AUDIENCE_KEYS.every((k) => audience[k]), [audience]);

  // content
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const TITLE_MAX = 50, MSG_MAX = 200;

  // derived mappers
  const selectedChannels = useMemo(() => Object.entries(channels).filter(([, v]) => v).map(([k]) => k), [channels]);
  const selectedAudience = useMemo(() => Object.entries(audience).filter(([, v]) => v).map(([k]) => k), [audience]);
  const apiChannels = useMemo(() => {
    const map = { sms: 'sms', email: 'email', inApp: 'in_app' };
    return selectedChannels.map((k) => map[k]).filter(Boolean);
  }, [selectedChannels]);
  const apiAudience = useMemo(() => {
    const map = {
      admins:       'admin',
      generalUsers: 'user',
      merchants:    'merchant',
      drivers:      'driver',
      finance:      'finance',
      organizers:   'organizer',
    };
    return selectedAudience.map((k) => map[k]).filter(Boolean);
  }, [selectedAudience]);

  // history
  const [history, setHistory] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  async function fetchAll() {
    if (!SYSTEM_NOTIFICATIONS_ALL) return;
    setLoadingList(true);
    try {
      const data = await apiFetch(SYSTEM_NOTIFICATIONS_ALL, {
        method: 'GET',
      });

      const rows =
        (Array.isArray(data) && data) ||
        (Array.isArray(data?.data) && data.data) ||
        (Array.isArray(data?.notifications) && data.notifications) ||
        (Array.isArray(data?.rows) && data.rows) ||
        (Array.isArray(data?.records) && data.records) ||
        [];

      const normalized = rows.map((r, i) => ([
        r.id ?? i,
        r.title ?? '',
        r.message ?? '',
        toArray(r.delivery_channels).map(backPrettyChan),
        toArray(r.target_audience).map(backPrettyAud),
        r.status ?? 'sent',
        r.created_at || r.sent_at || new Date().toISOString(),
      ])).map(([id, title, message, channels, audience, status, when]) => ({
        id, title, message, channels, audience, status, when,
      }));

      setHistory(normalized);
    } catch (e) {
      console.error('[Notifications] list failed:', e);
      setHistory([]);
    } finally {
      setLoadingList(false);
    }
  }

  useEffect(() => {
    (async () => {
      const ident = await ensureIdentity();
      setIdentity(ident);
      fetchAll();
    })();
  }, []);

  // submit
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanTitle = title.trim();
    const cleanMsg = message.trim();

    if (!cleanTitle) { Swal.fire({ icon: 'error', title: 'Please enter a title.' }); return; }
    if (!cleanMsg) { Swal.fire({ icon: 'error', title: 'Please enter the message content.' }); return; }
    if (apiChannels.length === 0) { Swal.fire({ icon: 'error', title: 'Select at least one delivery channel.' }); return; }
    if (apiAudience.length === 0) { Swal.fire({ icon: 'error', title: 'Select at least one target audience.' }); return; }
    if (!SYSTEM_NOTIFICATIONS) {
      Swal.fire({ icon: 'error', title: 'Endpoint not configured', text: 'SYSTEM_NOTIFICATIONS is empty. Check your .env and restart the dev server.' });
      return;
    }

    // final identity
    const { user_id, user_name } = identity.user_id ? identity : await ensureIdentity();

    const body = {
      ...(Number.isFinite(user_id) && user_id > 0 ? { user_id, admin_id: user_id, performed_by_id: user_id } : {}),
      ...(user_name ? { user_name, admin_name: user_name, performed_by: user_name } : {}),
      title: cleanTitle,
      message: cleanMsg,
      delivery_channels: apiChannels,
      target_audience: apiAudience,
    };

    // optimistic row
    const optimistic = {
      id: `optim-${Date.now()}`,
      title: body.title,
      message: body.message,
      channels: selectedChannels.map((c) => prettyChannel[c] || c),
      audience: selectedAudience.map((a) => prettyAudience[a] || a),
      when: new Date().toISOString(),
      status: 'pending',
    };
    setHistory((prev) => [optimistic, ...prev].slice(0, 100));

    // delayed loader (only show if it takes > 2s)
    let shownLoader = false;
    const loaderTimer = setTimeout(() => {
      shownLoader = true;
      Swal.fire({
        title: 'Sending…',
        html: 'Please wait while we deliver your notification.',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
    }, 2000);

    setSubmitting(true);
    try {
      await apiFetch(SYSTEM_NOTIFICATIONS, {
        method: 'POST',
        body,
      });

      clearTimeout(loaderTimer);
      if (shownLoader) Swal.close();

      await Swal.fire({ icon: 'success', title: 'Notification sent!' });

      // reset inputs
      setTitle('');
      setMessage('');
      setChannels({ sms: false, email: false, inApp: false });
      setAudience({ admins: false, generalUsers: false, merchants: false, drivers: false, finance: false, organizers: false });

      // reload fresh list (authoritative)
      await fetchAll();
      logActivity?.(`Sent notification • "${cleanTitle}"`);
    } catch (err) {
      clearTimeout(loaderTimer);
      if (shownLoader) Swal.close();

      // mark optimistic as failed
      setHistory((prev) => {
        const [first, ...rest] = prev;
        return [{ ...first, status: 'failed' }, ...rest];
      });
      console.error('[Notifications] send failed:', err);
      Swal.fire({ icon: 'error', title: 'Failed to send', text: String(err?.message || err) });
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- Export ALL records (not just the 20 shown) ---------- */
  const exportAllToExcel = () => {
    const now = new Date();
    const ts = now.toISOString().replace(/[:.-]/g, '').slice(0, 15); // YYYYMMDDTHHMMSS
    const fname = `system_notifications_${ts}.csv`;

    const headerLines = [
      ['Report Title', 'System Notifications'],
      ['Exported At', now.toLocaleString()],
      []
    ].map(row => row.map(csvEscape).join(','));

    const tableHeader = [
      'SI.No', 'Delivery Channels', 'Target Audience', 'Message Title',
      'Message Content', 'Status', 'When'
    ].map(csvEscape).join(',');

    const rows = history.map((row, idx) => ([
      String(idx + 1),
      (row.channels || []).join('; '),
      (row.audience || []).join('; '),
      row.title ?? '',
      row.message ?? '',
      row.status ?? 'sent',
      new Date(row.when || row.created_at || Date.now()).toLocaleString(),
    ].map(csvEscape).join(',')));

    downloadCSV(fname, [...headerLines, tableHeader, ...rows]);
  };

  // Edge-to-edge table within the card
  const wideEdge = { marginInline: 'clamp(-16px, -2.5vw, -32px)' };
  const TOP_PAD = { paddingInline: 'clamp(16px, 2.5vw, 32px)' };

  return (
    <div className="p-4 flex-grow-1">
      <div className="card p-4 system-card">
        <h4 className="section-title">System Notifications</h4>
        <p className="text-muted mb-4">
          Choose delivery channels, pick target audience, and add a title and message.
        </p>

        <form onSubmit={handleSubmit} className="settings-form">
          {/* Delivery Channels */}
          <div className="mb-4">
            <h5 className="mb-2">Delivery Channels</h5>
            {[
              { key: 'sms', label: 'SMS' },
              { key: 'email', label: 'Email' },
              { key: 'inApp', label: 'In App' },
            ].map(({ key, label }) => (
              <div className="form-check mb-2" key={key}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`ch-${key}`}
                  checked={channels[key]}
                  onChange={() => setChannels((p) => ({ ...p, [key]: !p[key] }))}
                />
                <label className="form-check-label" htmlFor={`ch-${key}`}>{label}</label>
              </div>
            ))}
            <small className="text-muted d-block">Select one or more delivery channels.</small>
          </div>

          {/* Target Audience */}
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between">
              <h5 className="mb-2">Target Audience</h5>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary w-auto px-3 py-1 rounded-3 btn-select-all"
                onClick={() => {
                  const next = !allAudienceSelected;
                  setAudience({ admins: next, generalUsers: next, merchants: next, drivers: next });
                }}
              >
                {allAudienceSelected ? 'Unselect All' : 'Select All'}
              </button>
            </div>

            {[
              { key: 'admins',       label: 'Admins' },
              { key: 'generalUsers', label: 'General Users (Customers, Passengers)' },
              { key: 'merchants',    label: 'Merchants' },
              { key: 'drivers',      label: 'Drivers' },
              { key: 'finance',      label: 'Finance' },
              { key: 'organizers',   label: 'Organizers' },
            ].map(({ key, label }) => (
              <div className="form-check mb-2" key={key}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`aud-${key}`}
                  checked={audience[key]}
                  onChange={() => setAudience((p) => ({ ...p, [key]: !p[key] }))}
                />
                <label className="form-check-label" htmlFor={`aud-${key}`}>{label}</label>
              </div>
            ))}
          </div>

          {/* Message Content */}
          <div className="mb-3">
            <label className="form-label d-flex justify-content-between">
              <span>Title</span>
              <small className={title.length > TITLE_MAX ? 'text-danger' : 'text-muted'}>
                {title.length}/{TITLE_MAX}
              </small>
            </label>
            <input
              type="text"
              className="form-control"
              maxLength={TITLE_MAX}
              placeholder="e.g., Scheduled Maintenance Tonight"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="mb-4">
            <label className="form-label d-flex justify-content-between">
              <span>Message Content</span>
              <small className={message.length > MSG_MAX ? 'text-danger' : 'text-muted'}>
                {message.length}/{MSG_MAX}
              </small>
            </label>
            <textarea
              className="form-control"
              rows="3"
              maxLength={MSG_MAX}
              placeholder="Write the message users will see…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          <div className="mb-4 border preview-card p-3 contrast-surface">
            <div className="preview-header mb-2"><strong>Preview</strong></div>
            <div className="preview-kv">
              <div className="preview-label">Title:</div>
              <div>{title || <em className="text-muted">No title</em>}</div>

              <div className="preview-label">Channels:</div>
              <div className="preview-chips">
                {selectedChannels.length
                  ? selectedChannels.map((c) => <span key={c} className="chip channel">{prettyChannel[c]}</span>)
                  : <em className="text-muted">None</em>}
              </div>

              <div className="preview-label">Audience:</div>
              <div className="preview-chips">
                {selectedAudience.length
                  ? selectedAudience.map((a) => <span key={a} className="chip audience">{prettyAudience[a]}</span>)
                  : <em className="text-muted">None</em>}
              </div>

              <div className="preview-label">Message:</div>
              <div>{message || <em className="text-muted">No message</em>}</div>
            </div>
          </div>

          <button type="submit" className="btn btn-primary w-100" disabled={submitting}>
            {submitting ? 'Sending…' : 'Notify'}
          </button>
        </form>

        {/* History Table (edge-to-edge) */}
        <div className="mt-4" style={wideEdge}>
          <div className="d-flex align-items-center justify-content-between mb-2" style={TOP_PAD}>
            <h5 className="mb-0">Recent Notifications</h5>
            <button
              type="button"
              className="btn btn-outline-success btn-sm px-3 export-btn"
              onClick={exportAllToExcel}
              title="Export all notifications to Excel"
            >
              Export Excel
            </button>
          </div>

          <div className="table-responsive contrast-surface" style={TOP_PAD}>
            <table className="table table-sm align-middle contrast-table w-100">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>SI.No</th>
                  <th>Delivery Channels</th>
                  <th>Target Audience</th>
                  <th>Message Title</th>
                  <th>Message Content</th>
                  <th>Status</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {loadingList ? (
                  <tr><td colSpan={7} className="text-muted text-center py-3">Loading…</td></tr>
                ) : history.length === 0 ? (
                  <tr><td colSpan={7} className="text-muted text-center py-3">No notifications yet.</td></tr>
                ) : (
                  /* show only first 20 rows */
                  history.slice(0, 20).map((row, idx) => (
                    <tr key={row.id ?? idx}>
                      <td>{idx + 1}</td>
                      <td>{(row.channels || []).join(', ')}</td>
                      <td>{(row.audience || []).join(', ')}</td>
                      <td className="fw-semibold">{row.title}</td>
                      <td style={{ whiteSpace: 'pre-wrap' }}>{row.message}</td>
                      <td>
                        {String(row.status).toLowerCase() === 'failed' && <span className="badge bg-danger">Failed</span>}
                        {String(row.status).toLowerCase() === 'pending' && <span className="badge bg-secondary">Pending</span>}
                        {(!row.status || String(row.status).toLowerCase() === 'sent') && <span className="badge bg-success">Sent</span>}
                      </td>
                      <td><small className="text-muted">
                        {new Date(row.when || row.created_at || Date.now()).toLocaleString()}
                      </small></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <footer className="dashboard-footer mt-4">
        <small>© {new Date().getFullYear()} <strong>སྟབས་བདེ།</strong> Admin • All rights reserved</small>
      </footer>
    </div>
  );
}
